import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import './About.css';

const About = () => {
  useEffect(() => {
    document.title = 'About - SOFTORA Technologies';
  }, []);

  const values = [
    {
      icon: "💎",
      title: "Engineering Craftsmanship",
      desc: "We write clean, well-architected code that is maintainable, scalable, and built strictly to international standards."
    },
    {
      icon: "🤝",
      title: "Client-Centric Collaboration",
      desc: "We don't act as detached vendors; we integrate directly with your product vision as dedicated technical partners."
    },
    {
      icon: "⚡",
      title: "Agile & Transparent",
      desc: "Weekly sprint check-ins, continuous code deployments, and milestone tracking ensure zero surprises."
    },
    {
      icon: "🛡️",
      title: "Integrity & Ownership",
      desc: "Full Intellectual Property (IP) ownership, complete source code handover, and post-launch maintenance guarantee."
    }
  ];

  const milestones = [
    { year: "Phase 1", title: "Inception & Core Focus", desc: "Started as a passionate engineering collective specializing in React and mobile frameworks." },
    { year: "Phase 2", title: "Rapid Scale & Portfolio Growth", desc: "Surpassed 30+ completed software projects with 100% on-time milestone delivery." },
    { year: "Phase 3", title: "Enterprise & Cloud Expansion", desc: "Integrated Firebase Cloud architectures, advanced backend microservices, and AI integrations." },
    { year: "Today", title: "Trusted Digital Partner", desc: "50+ live projects powering startups, retail businesses, and enterprises across the globe." }
  ];

  return (
    <div className="about-page">
      <PageBanner
        badge="WHO WE ARE"
        title="Engineering Intelligent Software For The"
        highlight="Digital Era"
        subtitle="Softora is a premier software development company founded with a passion for craftsmanship, clean architecture, and rapid technological innovation."
      />

      {/* Story Section */}
      <section className="about-story-section">
        <div className="about-container">
          <div className="about-story-grid">
            <div className="about-story-text">
              <span className="section-eyebrow">OUR STORY</span>
              <h2 className="about-section-heading">
                Driven by Passion, Defined by <span className="title-gradient">Results</span>
              </h2>
              <p className="about-paragraph">
                Founded in Sri Lanka, <strong>Softora</strong> emerged from a clear vision: to bridge the gap between high-complexity technology and high-growth business needs. We recognized that businesses need more than just code—they need strategic technology partners who care deeply about usability, scalability, and long-term performance.
              </p>
              <p className="about-paragraph">
                Over the years, our team has grown into a dynamic squad of engineers, UI/UX designers, and systems architects. Whether building a modern React web application, a high-performance Flutter mobile app, or a secure Firebase cloud infrastructure, Softora delivers future-proof solutions.
              </p>
              
              <div className="about-stats-strip">
                <div className="about-stat-item">
                  <span className="about-stat-num">50+</span>
                  <span className="about-stat-lbl">Projects Launched</span>
                </div>
                <div className="about-stat-item">
                  <span className="about-stat-num">99%</span>
                  <span className="about-stat-lbl">Client Satisfaction</span>
                </div>
                <div className="about-stat-item">
                  <span className="about-stat-num">100%</span>
                  <span className="about-stat-lbl">Code Handover</span>
                </div>
              </div>
            </div>

            <div className="about-story-card">
              <div className="about-card-glow"></div>
              <div className="mission-vision-box">
                <div className="mv-item">
                  <div className="mv-badge">OUR MISSION</div>
                  <h3>Empower Through Scalable Engineering</h3>
                  <p>
                    To empower businesses, startups, and innovators worldwide with modern, elegant, and secure digital software solutions that drive real commercial value.
                  </p>
                </div>
                <div className="mv-divider"></div>
                <div className="mv-item">
                  <div className="mv-badge">OUR VISION</div>
                  <h3>To Be the Premier Global Tech Studio</h3>
                  <p>
                    To be recognized as a world-class technology partner celebrated for engineering excellence, reliable delivery, and transformative digital experiences.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="about-values-section">
        <div className="about-container">
          <div className="text-center section-header">
            <span className="section-eyebrow">WHAT GUIDES US</span>
            <h2 className="about-section-heading">
              Our Core <span className="title-gradient">Values</span>
            </h2>
            <p className="about-subtitle">
              Every line of code we write and every architecture we deploy is grounded in these foundational principles.
            </p>
          </div>

          <div className="values-grid">
            {values.map((val, idx) => (
              <div key={idx} className="value-card">
                <div className="value-icon">{val.icon}</div>
                <h3 className="value-title">{val.title}</h3>
                <p className="value-desc">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey & Milestones */}
      <section className="about-journey-section">
        <div className="about-container">
          <div className="text-center section-header">
            <span className="section-eyebrow">OUR EVOLUTION</span>
            <h2 className="about-section-heading">
              The Journey of <span className="title-gradient">Softora</span>
            </h2>
          </div>

          <div className="timeline-grid">
            {milestones.map((ms, idx) => (
              <div key={idx} className="timeline-item">
                <div className="timeline-badge">{ms.year}</div>
                <h4 className="timeline-title">{ms.title}</h4>
                <p className="timeline-desc">{ms.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Box */}
      <section className="about-cta-section">
        <div className="about-container">
          <div className="about-cta-card">
            <h2>Ready to bring your project to life?</h2>
            <p>Speak directly with our technical leads and receive a comprehensive project roadmap.</p>
            <Link to="/contact" className="btn-primary">
              <span>GET IN TOUCH WITH US →</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
